import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TrendingDown, ArrowUp, ArrowDown, Minus, AlertCircle } from "lucide-react";

const STATUS_STYLES = {
  good: "bg-emerald-100 text-emerald-800 border-emerald-200",
  ok: "bg-amber-50 text-amber-800 border-amber-200",
  attention: "bg-rose-100 text-rose-800 border-rose-300",
  na: "bg-slate-50 text-slate-400 border-slate-200",
};

const STATUS_LABELS = {
  good: "On track",
  ok: "Watch",
  attention: "Needs attention",
  na: "No data",
};

export function ClassScoreboard() {
  const [term, setTerm] = useState(1);
  const [grade, setGrade] = useState("");
  const [section, setSection] = useState("");
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { term };
      if (grade) params.grade = grade;
      if (section) params.section = section;
      const { data } = await http.get("/class-scoreboard", { params });
      setBoard(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load scoreboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [term, grade, section]);

  const topAttention = useMemo(() => {
    if (!board) return [];
    return [...(board.metrics || [])]
      .map((m) => ({ m, count: board.attention_counts[m.id] || 0 }))
      .filter((x) => x.count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [board]);

  return (
    <div className="space-y-6">
      {/* Controls */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Term</Label>
              <div className="mt-1 flex rounded-lg border border-slate-200 overflow-hidden">
                {[1, 2].map((t) => (
                  <button
                    key={t}
                    data-testid={`scoreboard-term-${t}`}
                    onClick={() => setTerm(t)}
                    className={`px-4 h-9 text-sm font-medium transition-colors ${term === t ? "bg-sky-500 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
                  >
                    T{t}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Grade</Label>
              <Input data-testid="scoreboard-grade-input" placeholder="all" value={grade} onChange={(e) => setGrade(e.target.value)} className="mt-1 h-9 w-24" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Section</Label>
              <Input data-testid="scoreboard-section-input" placeholder="all" value={section} onChange={(e) => setSection(e.target.value)} className="mt-1 h-9 w-20" />
            </div>
            <div className="ml-auto flex items-center gap-3 text-xs text-slate-500">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-200 border border-emerald-300"></span>On track</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-200 border border-amber-300"></span>Watch</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-rose-300 border border-rose-400"></span>Attention</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {board && (
        <>
          {/* Top attention list */}
          {topAttention.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-base flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-rose-500" /> Metrics needing most attention
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {topAttention.map(({ m, count }) => (
                    <Badge
                      key={m.id}
                      data-testid={`attention-${m.id}`}
                      variant="outline"
                      className="border-rose-200 text-rose-700 bg-rose-50"
                    >
                      {m.name} · {count} kid{count > 1 ? "s" : ""}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Heatmap */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-base">
                  Class Scoreboard · Term {board.scope.term}
                </CardTitle>
                <Badge variant="outline">{board.total_students} students</Badge>
              </div>
              <div className="text-xs text-slate-500 mt-1">Each cell is coloured relative to class average, with metric direction in mind.</div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="scoreboard-heatmap">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold text-slate-500 uppercase tracking-widest text-[10px] sticky left-0 bg-slate-50">Student</th>
                      {board.metrics.map((m) => (
                        <th key={m.id} className="px-2 py-2 text-slate-500" title={`${m.name} · WHO: ${m.who_target}`}>
                          <div className="flex flex-col items-center gap-0.5">
                            <span className="font-semibold uppercase tracking-wider text-[10px] whitespace-nowrap max-w-[80px] truncate">{m.name}</span>
                            <DirectionIcon dir={board.direction[m.name]} />
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr><td colSpan={board.metrics.length + 1} className="py-10 text-center text-slate-400">Loading…</td></tr>
                    )}
                    {!loading && board.students.map((s) => (
                      <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50/40">
                        <td className="px-3 py-1.5 sticky left-0 bg-white group-hover:bg-slate-50/40 font-medium text-slate-800 min-w-[180px]">
                          <div className="flex flex-col leading-tight">
                            <span>{s.name}</span>
                            <span className="text-[10px] font-mono text-slate-400">{s.grade}{s.section} · {s.kid_id}</span>
                          </div>
                        </td>
                        {board.metrics.map((m) => {
                          const cell = s.cells[m.id] || { value: null, status: "na" };
                          return (
                            <td key={m.id} className="p-1 text-center">
                              <div
                                data-testid={`cell-${s.id}-${m.id}`}
                                title={`${m.name}: ${cell.value ?? "—"} · ${STATUS_LABELS[cell.status]}`}
                                className={`w-full h-8 rounded-md border font-mono tabular-nums flex items-center justify-center ${STATUS_STYLES[cell.status]}`}
                              >
                                {cell.value ?? "—"}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    {/* Class avg row */}
                    {!loading && board.students.length > 0 && (
                      <tr className="border-t-2 border-slate-300 bg-sky-50/50">
                        <td className="px-3 py-2 sticky left-0 bg-sky-50/50 font-semibold text-sky-900 text-[11px] uppercase tracking-widest">Class Avg</td>
                        {board.metrics.map((m) => (
                          <td key={m.id} className="p-1 text-center">
                            <div className="font-mono tabular-nums text-sky-700 font-semibold text-[11px]">
                              {board.class_avg[m.id] ?? "—"}
                            </div>
                          </td>
                        ))}
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function DirectionIcon({ dir }) {
  if (dir === "up") return <ArrowUp className="w-3 h-3 text-emerald-500" aria-label="higher is better" />;
  if (dir === "down") return <ArrowDown className="w-3 h-3 text-rose-500" aria-label="lower is better" />;
  return <Minus className="w-3 h-3 text-slate-400" aria-label="neutral" />;
}
